import { CONFIG } from "../constants.js";
import { adjustBackgroundQuality } from "../systems/BackgroundLifecycle.js";
/** @typedef {import('../game.js').AIHorizon} AIHorizon */

/**
 * Apply a performance tier to the running game instance.
 *
 * Adjusts particle budgets, starfield density, DPR ceilings, engine trail cadence,
 * and low-power flag. Tier changes are applied in place so they are not visually disruptive
 * mid-game: the canvas is only resized when the DPR ceiling actually changed, and the starfield
 * is thinned/thickened rather than regenerated (nebula is left untouched).
 *
 * Level semantics:
 *  - level 0: baseline (no throttling, full quality)
 *  - level N>0: apply merged profile overrides from CONFIG.PERFORMANCE.LEVELS[N-1]
 *
 * @param {AIHorizon} game Game instance to mutate.
 * @param {number} level 1-based performance level (0 = baseline).
 * @param {{ reinitialize?: boolean, force?: boolean, initial?: boolean, averageFrameMs?: number, workMs?: number }} [meta]
 *  reinitialize: default true; apply DPR / starfield changes to the live canvas & background if not explicitly false.
 *  force: ignore early-return when level unchanged.
 *  initial: treated like force but annotated for logging context.
 *  averageFrameMs / workMs: optional diagnostics used in the log message.
 */
export function applyPerformanceProfile(game, level, meta = {}) {
  const prevLevel = game._performanceLevel;
  const prevDprOverride = game._dprOverride;
  const shouldForce = meta.force === true || meta.initial === true;
  if (!shouldForce && level === prevLevel) return;

  const perf = CONFIG.PERFORMANCE || {};
  const defaults = {
    dprMax: CONFIG.VIEW.DPR_MAX,
    starfieldScale: 1,
    particleMultiplier: 1,
    particleBudget: Number.POSITIVE_INFINITY,
    engineTrailModulo: 1,
  };
  const levels = Array.isArray(perf.LEVELS) ? perf.LEVELS : [];
  const idx = Math.max(0, Math.min(level - 1, levels.length - 1));
  const profile = level > 0 && levels.length > 0 ? { ...defaults, ...levels[idx] } : defaults;
  const minStarfieldScale = Math.max(0, perf.MIN_STARFIELD_SCALE || 0.35);

  game._performanceLevel = level;
  game._starfieldScale = Math.max(minStarfieldScale, Math.min(1, profile.starfieldScale || 1));
  game._performanceParticleMultiplier = Math.max(0.1, Math.min(1, profile.particleMultiplier || 1));
  game._particleBudget = Number.isFinite(profile.particleBudget)
    ? Math.max(200, profile.particleBudget)
    : Number.POSITIVE_INFINITY;
  const overrideDpr = typeof profile.dprMax === "number" ? Math.max(0.75, profile.dprMax) : null;
  game._dprOverride = level > 0 ? overrideDpr : null;
  game._engineTrailModulo = Math.max(1, Math.round(profile.engineTrailModulo || 1));
  game._isLowPowerMode = level > 0;
  game._engineTrailStep = 0;

  if (game.particles && game.particlePool && game.particles.length > game._particleBudget) {
    const excess = game.particles.splice(game._particleBudget);
    for (const particle of excess) {
      game.particlePool.release(particle);
    }
  }

  if (meta.reinitialize !== false) {
    if (prevDprOverride !== game._dprOverride) {
      try {
        game.resizeCanvas();
      } catch {
        /* ignore */
      }
    }
    try {
      adjustBackgroundQuality(game);
    } catch {
      /* ignore */
    }
  }

  if (level !== prevLevel && typeof console !== "undefined" && console.info) {
    const avg = typeof meta.averageFrameMs === "number" ? meta.averageFrameMs.toFixed(1) : null;
    const work = typeof meta.workMs === "number" ? meta.workMs.toFixed(1) : null;
    const suffix = avg ? ` (avg frame ${avg}ms${work ? `, work ${work}ms` : ""})` : "";
    console.info(`[Performance] Adjusted to level ${level}${suffix}`);
  }
}
